import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { parameterizableSecretBaseToModel, parameterizablePasswordOrAKVtoSecretBase } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { PostgreSqlV2LinkedService } from 'app/model/linkedservice/postgre-sql-v2.linkedservice';
import { ValueAdapter } from '../value.adapter';
import { additionalFieldsAllowedKeys, PostGreSqlAdditionalPropertiesKeyType } from 'app/registries/consts/postgre-consts';
import { AuthenticationType, ParameterConfig } from 'shell-models-lib';
import { toParameterClass, toParameterObject } from './parameter-config.util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(PostgreSqlV2LinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                server: '*',
                database: '*',
                username: '*',
                port: '*',
                password: 'secret',
                encryptedCredential: '*',
                sslMode: '*',
                authenticationType: '*',
                // additional fields
                schema: '*',
                pooling: '*',
                connectionTimeout: '*',
                commandTimeout: '*',
                trustServerCertificate: '*',
                sslCertificate: '*',
                sslKey: '*',
                sslPassword: '*',
                readBufferSize: '*',
                logParameters: '*',
                timezone: '*',
                encoding: '*'
            },
            parameters: '*'
        }
    }
})
export class PostgreSqlV2LinkedServiceAdapter extends LinkedServiceAdapter<PostgreSqlV2LinkedService> {
    protected supportFirstClassParameterExperience = true;

    protected modelConstructor(): new () => PostgreSqlV2LinkedService {
        return PostgreSqlV2LinkedService;
    }

    protected model(): IModel {
        return PostgreSqlV2LinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: PostgreSqlV2LinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.PostgreSqlV2LinkedService;
        properties.type = Metamodel.modelFor(PostgreSqlV2LinkedService).name;
        properties.typeProperties = {
            server: this.valueToResource(linkedService, PostgreSqlV2LinkedService.SERVER_NAME),
            port: this.valueToResource(linkedService, PostgreSqlV2LinkedService.PORT),
            database: this.valueToResource(linkedService, PostgreSqlV2LinkedService.DATABASE_NAME),
            username: this.valueToResource(linkedService, PostgreSqlV2LinkedService.USER_NAME),
            password: parameterizablePasswordOrAKVtoSecretBase(linkedService, PostgreSqlV2LinkedService.PASSWORD, this.context),
            sslMode: this.valueToResource(linkedService, PostgreSqlV2LinkedService.SSL_MODE),
            authenticationType: linkedService.authenticationType
        };

        if (linkedService.parameterizableAdditionalFields?.length > 0) {
            properties.typeProperties = {
                ...properties.typeProperties,
                ...toParameterObject(linkedService.parameterizableAdditionalFields, false, this.valueToResource)
            };
        }
        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: PostgreSqlV2LinkedService): Promise<PostgreSqlV2LinkedService> {
        const prop = linkedServiceResource.properties as Model.PostgreSqlV2LinkedService;
        const typeProperties = prop.typeProperties;
        ValueAdapter.toModel(linkedService, PostgreSqlV2LinkedService.SERVER_NAME, typeProperties.server);
        ValueAdapter.toModel(linkedService, PostgreSqlV2LinkedService.PORT, typeProperties.port);
        ValueAdapter.toModel(linkedService, PostgreSqlV2LinkedService.DATABASE_NAME, typeProperties.database);
        ValueAdapter.toModel(linkedService, PostgreSqlV2LinkedService.USER_NAME, typeProperties.username);
        parameterizableSecretBaseToModel(linkedService, PostgreSqlV2LinkedService.PASSWORD, typeProperties.password, this.context);
        ValueAdapter.toModel(linkedService, PostgreSqlV2LinkedService.SSL_MODE, typeProperties.sslMode);
        linkedService.authenticationType = typeProperties.authenticationType || AuthenticationType.Basic;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const additionalPropertiesObj: { [key: string]: any } = {};
        for (const propertyName in typeProperties) {
            if (additionalFieldsAllowedKeys.includes(propertyName as PostGreSqlAdditionalPropertiesKeyType)) {
                additionalPropertiesObj[propertyName] =
                  typeProperties[propertyName as keyof Model.PostgreSqlV2LinkedServiceTypeProperties];
            }
        }
        linkedService.parameterizableAdditionalFields = toParameterClass(
            additionalPropertiesObj,
            linkedService
        ) as Array<ParameterConfig<PostGreSqlAdditionalPropertiesKeyType>>;

        return linkedService;
    }
}
